<!--
  TimeField — the grid renderer for time fields: a schema field of one kind
  (instant, event, hours, slot, lifetime) and the aggregate Times column.

  In the row, one line per time, latest first: a short row shows the latest
  time and a count, a medium row up to three lines, a tall row up to four with
  a detail line under each. At rest only the aggregate column, where kinds
  mix, leads each line with its kind's icon; a selected cell shows it on every
  field, as the editor does, so opening the editor barely moves the text.

  Editing a one-time field opens TimePicker; editing many opens TimesEditor.
  Neither sends a draft until it's confirmed (Set / Enter, or Done), so
  clicking away commits an unchanged draft and discards the edit. A tracked
  field (status changes) is written by core and stays read-only. The editors
  keep their clicks from the overlay, whose click starts an edit: Done would
  otherwise commit and then reopen it.
-->
<template>
  <div class="time-field" :class="{ 'is-editing': editing }">
    <template v-if="editing">
      <TimePicker
        v-if="!field.isArray"
        :value="rows[0] ?? null"
        :kind="kind ?? 'instant'"
        :seed="seed"
        :label="field.label"
        @click.stop
        @set="setOne"
        @clear="clearOne"
        @cancel="emit('cancel')"
      />
      <TimesEditor
        v-else
        :rows="rows"
        :kind="kind"
        :seed="seed"
        @click.stop
        @done="setMany"
        @cancel="emit('cancel')"
      />
    </template>

    <template v-else>
      <TimeElement
        v-for="row in shown"
        :key="row.id"
        :value="row"
        :show-kind="kind == null || mode === 'active'"
        :single="!field.isArray"
        :detail="rowHeight === 'tall'"
      />
      <span v-if="more" class="more">+{{ more }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue"
import { isRelationDelta } from "@auger/core/collections"
import { type AugerField, referenceEntities } from "@auger/core/fields"
import TimeElement from "@auger/ui/components/elements/TimeElement.vue"
import { GRID_ROWS_CLASS } from "@auger/ui/composables/useGrid"
import { CELL_CONTEXT } from "@auger/ui/composables/useInteraction"
import { type FieldMode, type FieldSize } from "@auger/ui/fields/useField"
import { latestFirst, type TimeKind, type TimeRow } from "@auger/ui/utils/times"
import TimePicker from "@/components/times/TimePicker.vue"
import TimesEditor from "@/components/times/TimesEditor.vue"
import type { TimesDelta } from "@/composables/useTimesEditor"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: AugerField
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const cell = inject(CELL_CONTEXT, null)
const rowsClass = inject(GRID_ROWS_CLASS, null)

const toRows = (value: unknown): TimeRow[] =>
  (value == null ? [] : Array.isArray(value) ? value : [value]).filter(
    (r): r is TimeRow => !!r && typeof r === "object" && "start_at" in r
  )

// The field's rows as stored. Outside an edit the value is those rows; while
// editing it is this cell's draft (or a typed key), so read them off the row.
const rows = computed<TimeRow[]>(() => {
  const v = props.modelValue
  if (!isRelationDelta(v) && typeof v !== "string") return latestFirst(toRows(v))
  const item = cell?.item()
  return item ? latestFirst(toRows(referenceEntities(props.field, item))) : []
})

const seed = computed(() => (typeof props.modelValue === "string" ? props.modelValue : ""))

/** The kind this field holds, or null where any kind goes (aggregate Times). */
const kind = computed<TimeKind | null>(() =>
  props.field.type === "time" ? null : (props.field.type as TimeKind)
)

const writable = computed(() => !!props.field.isEditable && !props.field.tracks && !!cell?.writer())
const editing = computed(() => props.mode === "editing" && writable.value)

// --- In the row -----------------------------------------------------------------

/** A grid row's height preset, or medium outside a grid (a card). */
const rowHeight = computed(() => {
  const c = rowsClass?.value ?? "is-medium-rows"
  return c === "is-short-rows" ? "short" : c === "is-tall-rows" ? "tall" : "medium"
})

// Room for this many lines; when there are more, one line goes to the count.
const room = computed(() => ({ short: 1, medium: 3, tall: 4 })[rowHeight.value])
const shown = computed(() => {
  const all = rows.value
  if (all.length <= room.value) return all
  return all.slice(0, Math.max(1, room.value - (rowHeight.value === "short" ? 0 : 1)))
})
const more = computed(() => rows.value.length - shown.value.length)

// --- One time -------------------------------------------------------------------

/** Set the one time: an edit of its row when it has one, else a new row. */
function setOne(columns: {
  start_at: string
  end_at: string | null
  all_day: boolean
  timezone: string
}) {
  const row = rows.value[0]
  const delta = row?.id
    ? { update: [{ id: row.id, ...columns }] }
    : {
        add: [
          {
            timeType: kind.value ?? "instant",
            startAt: columns.start_at,
            endAt: columns.end_at,
            allDay: columns.all_day,
            timezone: columns.timezone,
          },
        ],
      }
  emit("update:modelValue", delta)
  emit("commit")
}

/** Done on the list: its delta is the draft, committed at once. */
function setMany(delta: TimesDelta) {
  emit("update:modelValue", delta)
  emit("commit")
}

function clearOne() {
  const row = rows.value[0]
  if (row?.id) emit("update:modelValue", { remove: [{ id: row.id }] })
  emit("commit")
}
</script>

<style>
.time-field {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  min-width: 0;
  /* The size every other cell's value reads at. */
  font-size: var(--auger-size-7);

  /* How many more times than the row has room for: a small grey tag. */
  .more {
    flex: none;
    padding: 1px 6px;
    border-radius: var(--auger-radius);
    background: var(--auger-bg-muted);
    color: var(--auger-text-weak);
    font-size: 0.9em;
    font-weight: 500;
    line-height: 1.4;
  }
}

/* Each time's line, a touch taller than the text, so lines in a medium or
   tall row read apart. */
.time-field:not(.is-editing) .time-line {
  min-height: 20px;
}

/* A short row is one line: the latest time and the count beside it. */
.is-short-rows .time-field:not(.is-editing) {
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;

  /* The time gives way (ellipsised) so the count, pulled right, always shows. */
  .time-element {
    flex: 0 1 auto;
  }

  .more {
    margin-left: auto;
  }
}

.is-tall-rows .time-field:not(.is-editing) {
  justify-content: flex-start;
  gap: 4px;
}

/* Editing, the editor fills the overlay, which widens to fit it. */
.time-field.is-editing {
  align-items: stretch;
}

.grid-editor:has(> .time-field.is-editing) {
  width: min-content;
  max-width: 480px;
  /* Wider than its cell, so near the right edge it lines up with the cell's
     right side instead of running off the page. */
  position-try-fallbacks:
    flip-inline,
    flip-block,
    flip-block flip-inline;
}

.grid-editor > .time-field.is-editing:has(.rows-editor) {
  min-width: 420px;
}
</style>
